"use client";

import { useParams } from "next/navigation";

import { Link, usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";

/**
 * LocaleSwitch renders one anchor per locale. Anchor navigation is what
 * Safari (especially iOS Safari) handles most reliably — a previous
 * imperative `useRouter().replace()` from inside a button onClick was
 * silently failing on iOS, likely a stacking-context / touch-event
 * interaction with the sticky, backdrop-blurred header. <Link> with the
 * `locale` prop is the pattern next-intl itself recommends for this.
 *
 * With translated pathnames, `pathname` from next-intl's usePathname
 * is the *internal* route name (e.g. `/projects/[slug]`), and we pass
 * `params` from Next's useParams so Link can render the correct
 * localized URL in the target locale.
 */
export function LocaleSwitch({ current }: { current: string }) {
  const pathname = usePathname();
  const params = useParams();

  // The internal pathname can be a dynamic template (e.g. /projects/[slug]),
  // which next-intl can only localize if every [param] it needs is present.
  // Normally useParams() supplies them — but if a rewrite ever renders this
  // component on a different route than the URL, usePathname() (from the URL)
  // and useParams() (from the rendered route) disagree and the params are
  // missing. Passing an under-specified template to <Link> throws, so in
  // that case fall back to switching locale on "/" instead of crashing.
  const requiredParams = pathname.match(/\[\.{0,3}(\w+)\]/g) ?? [];
  const hasAllParams = requiredParams.every((token) => {
    const key = token.replace(/[[\].]/g, "");
    return params[key] != null;
  });
  const switchHref = (hasAllParams ? { pathname, params } : "/") as Parameters<
    typeof Link
  >[0]["href"];

  return (
    <div
      className="font-mono text-xs uppercase tracking-wider flex items-center"
      role="group"
      aria-label="Language"
    >
      {routing.locales.map((loc, i) => {
        const isActive = loc === current;
        return (
          <span key={loc} className="flex items-center">
            {i > 0 && (
              <span aria-hidden className="text-fg-subtle mx-1">
                /
              </span>
            )}
            {isActive ? (
              <span
                aria-current="true"
                className="text-fg select-none px-1 py-2 -my-2"
              >
                {loc}
              </span>
            ) : (
              <Link
                href={switchHref}
                locale={loc}
                prefetch={false}
                scroll={false}
                hrefLang={loc}
                className="text-fg-subtle hover:text-fg transition-colors px-1 py-2 -my-2 touch-manipulation"
              >
                {loc}
              </Link>
            )}
          </span>
        );
      })}
    </div>
  );
}
